import { inject, Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { StoryActions, StoryHttpActions } from '@common/store/story/actions/story.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { AppService } from '@src/suite/app.service';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { BasicContentService } from '@src/suite/common/services/basic-content-service';
import { MosEventService } from '@src/suite/common/services/mos-event.service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { StoryService } from '@src/suite/common/services/story-service';
import { FullscreenActions, OpenFullscreenModalActions, selectCurrentRecord, selectStoryRecordToEdit } from '@src/suite/common/store/fullscreen';
import { MerlinSearchActions } from '@src/suite/common/store/merlin/actions/ai.search.actions';
import { StoriesRecordActions } from '@src/suite/common/store/records/records.actions';
import { selectStoryRecordByGuid } from '@src/suite/common/store/records/records.selectors';
import { ML2HtmlUtils } from '@src/suite/common/utils/ml-2-html-utils';
import { parseMosMsg } from '@src/suite/common/utils/mos-utlis';
import { initializeRecord } from '@src/suite/common/utils/record-utils';
import { parseXml } from '@src/suite/common/utils/xml-utils';
import { PlaybookOpenAiService } from '@src/suite/playbook/services/playbook-open-ai-service';
import { PlaybookService } from '@src/suite/playbook/services/playbook-service';
import { catchError, exhaustMap, from, map, mergeMap, of, switchMap } from 'rxjs';

@Injectable()
export class StoryEffects {
    private playbookService = inject(PlaybookService);
    private nomWebServicesJson = inject(NomWebServicesJson);
    private storyService = inject(StoryService);
    private appService = inject(AppService);
    private actions$ = inject(Actions);
    private store = inject(Store);
    private playbookOpenAiService = inject(PlaybookOpenAiService);
    private basicContentService = inject(BasicContentService);
    private mosEventService = inject(MosEventService);

    // TODO: fix this any
    currentCommandDialogRef: MatDialogRef<any, any> = null;

    createStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StoryHttpActions.createNewVersionSuccess),
            exhaustMap(action => {
                const { topic } = action;
                const { Database } = topic;
                return from(this.playbookService.createNewStory(Database, topic, action?.generatedText, action.promptTitle)).pipe(
                    exhaustMap(res => {
                        if (res?.status) {
                            return from(
                                this.appService.basicContentService.updateBasicContentV2<StorytellingTopicRecord>(
                                    topic.Database,
                                    topic.Path.fullPath,
                                    topic.Guid,
                                    false,
                                    { Type: topic.Type },
                                    false,
                                    null
                                )
                            ).pipe(
                                exhaustMap(() => {
                                    // TODO: add story to record store
                                    const actions = [];
                                    actions.push(FullscreenActions.setLoadingProgressBar({ showLoader: false }));
                                    if (action.openNewPrompt) {
                                        actions.push(FullscreenActions.openStory({ recordGuid: res.data.Guid, recordType: ObjectTypes.SCRIPT }));
                                        return from(actions);
                                    }
                                    actions.push(FullscreenActions.openStory({ recordGuid: topic.stories[topic.stories.length - 1].Guid, recordType: ObjectTypes.SCRIPT }));
                                    return from(actions);
                                })
                            );
                        }
                    })
                );
            }),
            catchError(() => of(FullscreenActions.noOpAction()))
        );
    });

    loadPrompts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StoryHttpActions.loadPrompts),
            mergeMap(() => {
                return this.playbookOpenAiService.getPrompts().pipe(
                    map(data => {
                        return StoryHttpActions.loadPromptsSuccess({ data: data });
                    }),
                    catchError(error => {
                        return of(StoryHttpActions.loadPromptsError({ error: error }));
                    })
                );
            })
        );
    });

    generateNewVersion$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StoryHttpActions.generateNewVersion),
            concatLatestFrom(() => this.store.select(selectStoryRecordToEdit)),
            exhaustMap(([action, story]) => {
                const topic = action.topic;
                const promptTitle = action.prompt.title;
                return this.playbookOpenAiService.getCustomPrompt(story.text, action.prompt.value, 500).pipe(
                    map(generatedText => {
                        // TODO: check this, handle this case
                        const parsedStoryText = `<body>${generatedText?.replace(/(\r\n|\n)/g, '<br/>')}</body>`;
                        if (topic?.Guid) {
                            return StoryHttpActions.createNewVersionSuccess({ topic, generatedText: parsedStoryText, promptTitle, openNewPrompt: action.openNewPrompt });
                        } else {
                            const newStory = initializeRecord<StorytellingStoryRecord>(ObjectTypes.SCRIPT);
                            this.playbookService.initializeNewStoryRecordV2(newStory, topic);
                            const { text, textCommands } = ML2HtmlUtils.fixTextCommandsFromAIPrompt(parsedStoryText);
                            if (Object.keys(textCommands).length > 0) {
                                newStory.TextCommands = textCommands;
                            }
                            newStory.text = text;
                            newStory.Title = promptTitle;
                            // topic.addStory(newStory as StoryRecord);
                            return FullscreenActions.openStory({ recordGuid: topic.stories[topic.stories.length - 1].Guid, recordType: ObjectTypes.SCRIPT });
                        }
                    })
                );
            }),
            catchError(error => {
                return of(StoryHttpActions.createNewVersionError({ error: error }));
            })
        );
    });

    currentStoryChanged$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FullscreenActions.openStory, OpenFullscreenModalActions.openStoryFullscreenDialog),
            concatLatestFrom(() => this.store.select(selectCurrentRecord)),
            switchMap(([_, storyRecord]) => {
                const getStory = async (): Promise<StorytellingStoryRecord> => {
                    if (storyRecord.isNewRecord) {
                        return storyRecord;
                    } else {
                        return this.basicContentService
                            .getRecord<StorytellingStoryRecord>(storyRecord?.Database || this.nomWebServicesJson.getDatabase(), storyRecord.Path.fullPath, storyRecord.Guid)
                            .then(res => {
                                if (res?.status) {
                                    return res.data;
                                }
                                return null;
                            });
                    }
                };
                return from(getStory()).pipe(
                    switchMap(data => {
                        if (data) {
                            let trimmedText = data?.text?.trim() || '';
                            if (trimmedText === '<body></body>') {
                                trimmedText = '';
                            }
                            return of(MerlinSearchActions.generatePhotoAndVideoSearchTerm({ queryText: trimmedText }), MerlinSearchActions.searchEntity({ queryText: trimmedText }));
                        }
                        return of(FullscreenActions.noOpAction());
                    })
                );
            })
        );
    });

    createStoryTemplate$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(StoryHttpActions.createStoryTemplate),
                map(action => {
                    return from(this.basicContentService.createStoryTemplate(action.story.Database, action.path, false, null, action.story.Title, action.story.BeginText));
                })
            );
        },
        { dispatch: false }
    );

    releaseCurrentStoryLock$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StoryHttpActions.releaseStoryLock),
            switchMap(({ storyGuid }) => {
                if (!storyGuid) {
                    return of(FullscreenActions.noOpAction());
                }
                const story = this.store.selectSignal(selectStoryRecordByGuid(storyGuid))();
                const storyLockedByCurrentUser = () => story?.EditLocked && story.EditLockedBy === this.nomWebServicesJson.UserId;
                if (story && story.Guid && !story.Guid.startsWith('@') && storyLockedByCurrentUser()) {
                    return from(this.storyService.releaseStoryLock(story.Database || this.nomWebServicesJson.getDatabase(), story.Path?.fullPath, story.Guid)).pipe(
                        switchMap(() => {
                            return of(FullscreenActions.noOpAction());
                        }),
                        catchError(() => of(FullscreenActions.noOpAction()))
                    );
                }
                return of(FullscreenActions.noOpAction());
            })
        );
    });

    addMosItem$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(StoryActions.addMosItem),
                concatLatestFrom(() => [this.store.select(selectStoryRecordToEdit)]),
                map(([action, currentStory]) => {
                    if (!currentStory?.Guid || currentStory.Guid !== action.storyId) {
                        return FullscreenActions.noOpAction();
                    }
                    const commandText = action.mosStr.startsWith('[') && action.mosStr.endsWith(']') ? action.mosStr : `[${action.mosStr}]`;

                    // Emit through the service
                    this.mosEventService.emitMosItem({
                        storyGuid: currentStory.Guid,
                        mosCommand: commandText,
                    });
                })
            );
        },
        { dispatch: false }
    );

    replaceMosItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(StoryActions.replaceMosItem),
            concatLatestFrom(() => [this.store.select(selectStoryRecordToEdit)]),
            map(([action, currentStory]) => {
                // TODO: change this if statement
                if (!(currentStory?.Guid && currentStory.Guid === action.storyId && action.mosElId && action.mosStr && currentStory?.TextCommands[action.mosElId])) {
                    return FullscreenActions.noOpAction();
                }
                const textCommand = currentStory.TextCommands[action.mosElId];
                const oldMosXml = parseMosMsg(textCommand);
                const newMosXml = parseMosMsg(action.mosStr);
                const newMosObjId = newMosXml?.getElementsByTagName('objID')?.[0]?.textContent;
                const oldMosObjId = oldMosXml?.getElementsByTagName('objID')?.[0]?.textContent;
                if (!oldMosObjId || !newMosObjId || oldMosObjId !== newMosObjId) {
                    return FullscreenActions.noOpAction();
                }
                currentStory.TextCommands[action.mosElId] = action.mosStr.startsWith('[') && action.mosStr.endsWith(']') ? action.mosStr : `[${action.mosStr}]`;
                const newStory = { ...currentStory };
                let parsedStoryText = parseXml(newStory.text, 'text/html');
                if (!parsedStoryText?.body) {
                    parsedStoryText = parseXml('<html xmlns="http://www.w3.org/1999/xhtml"><body><p></p></body></html>', 'text/html');
                }

                const mosItemElement = ML2HtmlUtils.generateMosItemHtml(action.mosStr, action.mosElId);
                if (mosItemElement) {
                    const mosItem = parsedStoryText.getElementById(action.mosElId);
                    if (mosItem) {
                        mosItem.replaceWith(mosItemElement);
                    }
                    newStory.text = parsedStoryText.documentElement.outerHTML;
                } else {
                    console.log('Failed to parse mos item');
                }

                return StoriesRecordActions.updateStories({ records: [newStory] });
            })
        );
    });
}
